import Usercentrics, { BaseCategory, TRACKING_EVENT_TYPE, UserDecision } from '@usercentrics/cmp-browser-sdk';
import { CheckboxState } from '../types/CheckboxState';
import { ConsentByCategory } from '../types/ConsentByCategory';
import { ConsentObject } from '../types/ConsentObject';
import { ConsentStatus } from '../types/ConsentStatus';

export default abstract class ConsentHelper {
  public static acceptAllConsents(usercentrics: Usercentrics): Promise<void> {
    return usercentrics?.acceptAllServices();
  }

  public static saveConsentSelection(usercentrics: Usercentrics, consentData: UserDecision[]): Promise<void> {
    return usercentrics?.updateServices(consentData);
  }

  public static denyConsentsExceptRequired(usercentrics: Usercentrics): Promise<void> {
    return usercentrics?.denyAllServices();
  }

  public static trackEvent(usercentrics: Usercentrics, event: TRACKING_EVENT_TYPE): void {
    /*
     * Method to track events by UC. Documented in these URLs:
     * https://docs.usercentrics.com/cmp_browser_sdk/4.6.0/classes/default.html#setTrackingPixel
     * https://docs.usercentrics.com/cmp_browser_sdk/4.6.0/enums/TRACKING_EVENT_TYPE.html
     */
    return usercentrics?.setTrackingPixel({
      eventType: event,
      abTestVariant: '',
    });
  }

  public static getConsentsByCategory(categories: BaseCategory[], isBannerVisible: boolean): ConsentByCategory[] {
    let consentsbyCategories: ConsentByCategory[] = [];

    categories?.forEach((category) => {
      const allServicesAccepted = category?.services?.every((service) => service?.consent?.status);
      const categoryStatus = {
        isEssential: category.isEssential,
        name: category.label,
        slug: category.slug,
        userAccepted: !isBannerVisible && allServicesAccepted,
      };

      consentsbyCategories = [...consentsbyCategories, categoryStatus];
    });

    return consentsbyCategories;
  }

  public static consentsArrayToObjectConverter(consents: ConsentStatus[]): ConsentObject {
    return (
      consents?.reduce((previous, current) => {
        const newConsent = { [current?.name]: current?.enabled };
        return { ...previous, ...newConsent };
      }, {}) || {}
    );
  }

  public static getAllConsents(categories: BaseCategory[]): ConsentStatus[] {
    let consents: ConsentStatus[] = [];

    categories?.forEach((category) => {
      const services = category?.services?.map((service) => {
        const serviceName = service?.name
          .match(/^[\w\d\s{}}}\\\\{{{}"/-]+/g)[0]
          .trim()
          .replace(/\s/g, '_');
        return {
          name: serviceName,
          enabled: service?.consent?.status,
          id: service?.id,
        };
      });
      consents = [...consents, ...services];
    });

    return consents;
  }

  public static getUserDecision(categories: BaseCategory[]): UserDecision[] {
    let consents: UserDecision[] = [];

    categories?.forEach((category) => {
      const services = category?.services?.map((service) => {
        return {
          serviceId: service?.id,
          status: service?.consent?.status,
        };
      });
      consents = [...consents, ...services];
    });

    return consents;
  }

  public static getCheckboxClass(category: BaseCategory): string {
    const activeServices = category?.services?.filter((service) => service.consent.status);
    let state: CheckboxState = CheckboxState.Partial;
    if (activeServices?.length === category?.services?.length) {
      state = CheckboxState.All;
    } else if (!activeServices.length) {
      state = CheckboxState.None;
    }
    return state;
  }

  public static getCheckboxAllClass(consents: UserDecision[]): string {
    const consentsGiven = consents?.filter((consent) => consent.status);
    let state: CheckboxState = CheckboxState.Partial;
    if (consents?.length === consentsGiven?.length) {
      state = CheckboxState.All;
    } else if (!consentsGiven?.length) {
      state = CheckboxState.None;
    }
    return state;
  }

  public static getCategoryChecked(category: BaseCategory): boolean {
    const activeServices = category?.services?.filter((service) => service.consent.status);

    return activeServices?.length === category?.services?.length;
  }
}
